/**
 * Generic login hook supporting both Upercut and Pro users
 * Uses loginAccount query which supports all user types
 */

import { useLazyQuery, ApolloError } from '@apollo/client';
import { loginAccountQuery } from '@gql/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useApolloClient } from '../apollo';
import { Roles } from '@utils/roleUtils';

interface LoginResult {
    login: (email: string, password: string) => Promise<void>;
    loading: boolean;
    error?: ApolloError;
    data?: any;
}

interface UseLoginOptions {
    onCompleted?: (data: any) => void;
    onError?: (error: ApolloError) => void;
    redirectTo?: string | ((role: Roles) => string); // Can be a string or function based on role
}

/**
 * Generic login hook for all user types (Upercut, Pro, Talent, etc.)
 *
 * @example
 * const { login, loading, error } = useLogin({
 *   onCompleted: (data) => console.log('Logged in as', data.role),
 *   redirectTo: (role) => role === 'UPERCUT' ? '/dashboard' : '/pro/dashboard',
 * });
 */
export const useLogin = ({
    onCompleted,
    onError,
    redirectTo = '/',
}: UseLoginOptions): LoginResult => {
    const navigate = useNavigate();
    const { login: authLogin } = useAuth();
    const { updateClient } = useApolloClient();

    const [doLogin, { loading, error, data }] = useLazyQuery(loginAccountQuery, {
        onCompleted: (data) => {
            const loginData = data.loginAccount;
            const { token, role, changePassword, email } = loginData;

            // Store auth data using AuthContext
            authLogin(loginData);

            // Update Apollo Client with new token
            updateClient(token);

            // If user needs to reset password on first login, redirect to first login reset password page
            if (changePassword && role === Roles.UPERCUT) {
                navigate(`/auth/first-login-reset-password?email=${encodeURIComponent(email)}`);
                if (onCompleted) {
                    onCompleted(loginData);
                }
                return;
            }

            // Determine redirect path based on role
            let redirectPath = '/';
            if (typeof redirectTo === 'function') {
                redirectPath = redirectTo(role as Roles);
            } else if (typeof redirectTo === 'string') {
                redirectPath = redirectTo;
            }

            // Navigate to appropriate page
            if (redirectPath) {
                navigate(redirectPath);
            }

            // Call custom onCompleted handler
            if (onCompleted) {
                onCompleted(loginData);
            }
        },
        onError: (err) => {
            console.error('Login error:', err);
            if (onError) {
                onError(err);
            }
        },
    });

    const login = async (email: string, password: string) => {
        await doLogin({
            variables: { email, password },
        });
    };

    return { login, loading, error, data };
};

/**
 * Hook specifically for Pro user login
 * Convenience wrapper around useLogin
 */
export const useLoginPro = (options: Omit<UseLoginOptions, 'redirectTo'> = {}) => {
    return useLogin({
        ...options,
        redirectTo: '/pro/dashboard', // Default Pro dashboard route
    });
};

/**
 * Hook specifically for Upercut user login
 * Convenience wrapper around useLogin
 */
export const useLoginUpercut = (options: UseLoginOptions = {}) => {
    return useLogin({
        ...options,
        redirectTo: options.redirectTo || '/', // Default Upercut dashboard route
    });
};

export default useLogin;
